import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

// GET IT WORKING: single-form page. Submitting always shows the same success
// message regardless of whether the email exists (server enforces the
// enumeration-safe response too). The user is guided to check their inbox
// and reminded the link expires in 60 minutes.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err.message || "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-secondary/30 to-background px-4 py-10">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="forgot-back-to-login">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
            <Mail className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Reset your password</h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Enter the email on your SRCI Pharm-Route account and we'll send you a one-time link to choose a new password.
          </p>
        </div>

        {submitted ? (
          <Card className="p-6 space-y-3 text-center" data-testid="forgot-password-success">
            <div className="mx-auto h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              If <span className="font-medium text-foreground">{email}</span> is on file, a reset link is on its way. The link expires in 60 minutes and can only be used once.
            </p>
            <Button asChild variant="outline" className="mt-3">
              <Link to="/login">Return to sign in</Link>
            </Button>
          </Card>
        ) : (
          <Card className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-password-email"
                />
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="forgot-password-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy || !email}
                className="w-full h-11 rounded-full"
                data-testid="forgot-password-submit"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
